<script>
import api from "@/helpers/api-rest/api.js";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { enUS } from "date-fns/locale";
import simplebar from "simplebar-vue";

const ACTIVITY_ICON = "ri-folder-chart-line";

/**
 * Dashboard feed: latest registered essays (projects).
 */
export default {
  components: { simplebar },
  data() {
    return {
      items: [],
      isLoading: false,
      loadError: null,
      activityIcon: ACTIVITY_ICON,
    };
  },
  mounted() {
    this.loadRecentEssays();
  },
  methods: {
    formatActivityMeta(iso) {
      if (!iso) {
        return { datePart: "", timePart: "" };
      }
      const d = parseISO(iso);
      return {
        datePart: format(d, "dd MMM, yyyy", { locale: enUS }),
        timePart: format(d, "h:mm a", { locale: enUS }).toLowerCase(),
      };
    },
    essayRoute(item) {
      if (!item.project_id) {
        return null;
      }
      return {
        name: "essay-detail",
        params: {
          id: String(item.project_id),
          essayId: String(item.id),
        },
      };
    },
    canOpenEssay(item) {
      return Boolean(item.project_id);
    },
    essayTooltipConfig(text) {
      const t = text != null && String(text).length ? String(text) : "—";
      return {
        title: t,
        container: "body",
        boundary: "viewport",
      };
    },
    async loadRecentEssays() {
      this.isLoading = true;
      this.loadError = null;
      try {
        const response = await api.get({
          url: "/api/v1/dashboard/recent-essays",
          withLoading: false,
        });
        const raw = response?.data?.data?.items;
        const list = Array.isArray(raw) ? raw : [];
        this.items = list.map((row) => ({
          ...row,
          ...this.formatActivityMeta(row.created_at),
        }));
      } catch (error) {
        console.error("Error loading recent essays:", error);
        this.loadError = error;
        this.items = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="card">
    <div class="card-body">
      <h4 class="card-title mb-3">Últimos proyectos</h4>

      <div v-if="isLoading" class="text-center py-3 text-muted">
        Cargando…
      </div>
      <div
        v-else-if="loadError"
        class="alert alert-warning mb-0"
        role="alert"
      >
        No se pudo cargar el listado de proyectos recientes.
      </div>
      <div
        v-else-if="items.length === 0"
        class="text-muted text-center py-2"
      >
        No hay proyectos registrados aún.
      </div>

      <simplebar
        v-else
        class="recent-essays-scroll"
      >
        <ul class="list-unstyled activity-wid activity-wid--compact recent-essays-feed mb-0">
          <li
            class="activity-list"
            v-for="item in items"
            :key="item.id"
          >
            <div class="activity-icon avatar-xs">
              <span class="avatar-title bg-soft-primary text-primary rounded-circle">
                <i :class="activityIcon"></i>
              </span>
            </div>
            <div
              class="pl-0"
              style="min-width: 0"
            >
              <!--
                Do not use stretched-link here: it overlays the block and steals pointer events,
                so v-b-tooltip on children never receives hover.
              -->
              <component
                :is="canOpenEssay(item) ? 'router-link' : 'div'"
                v-bind="canOpenEssay(item) ? { to: essayRoute(item) } : {}"
                class="d-block text-reset text-decoration-none"
                style="min-width: 0"
              >
                <h5
                  class="font-size-13 mb-1 d-flex justify-content-between align-items-center flex-nowrap"
                >
                  <span class="mr-2" style="min-width: 0">
                    {{ item.datePart }}
                    <small class="text-muted">{{ item.timePart }}</small>
                  </span>
                  <span
                    v-if="item.essay_status_name"
                    class="badge badge-soft-primary font-size-11 flex-shrink-0 text-truncate essay-status-badge"
                    v-b-tooltip.hover="essayTooltipConfig(item.essay_status_name)"
                  >{{ item.essay_status_name }}</span>
                </h5>

                <div
                  class="font-size-14 mb-0 font-weight-semibold text-dark text-truncate"
                  v-b-tooltip.hover="essayTooltipConfig(item.name)"
                >
                  {{ item.name || "—" }}
                </div>

                <div
                  class="d-flex font-size-13 text-muted mb-0 mt-1"
                  style="min-width: 0"
                >
                  <span class="flex-shrink-0 mr-1">Convocatoria:</span>
                  <span
                    class="text-truncate min-width-0"
                    style="min-width: 0"
                    v-b-tooltip.hover="essayTooltipConfig(item.project_name)"
                  >{{ item.project_name || "—" }}</span>
                </div>

                <div
                  v-if="item.creator_name"
                  class="d-flex font-size-12 text-muted mt-1"
                  style="min-width: 0"
                >
                  <span class="flex-shrink-0 mr-1">Creado por:</span>
                  <span
                    class="text-truncate min-width-0"
                    style="min-width: 0"
                    v-b-tooltip.hover="essayTooltipConfig(item.creator_name)"
                  >{{ item.creator_name }}</span>
                </div>
              </component>
            </div>
          </li>
        </ul>
      </simplebar>
    </div>
  </div>
</template>

<style scoped>
/* Scroll area height for the activity list */
.recent-essays-scroll {
  max-height: 500px;
}

/*
 * Default .activity-list uses 40px bottom padding (_widgets.scss); tighten for this feed.
 */
.activity-wid--compact.activity-wid {
  margin-top: 4px;
  margin-left: 14px;
}

.activity-wid--compact .activity-list {
  padding: 0 0 14px 26px;
}

.activity-wid--compact .activity-list:before {
  top: 26px;
}

.activity-wid--compact .activity-list:last-child {
  padding-bottom: 0;
}

/* Keep status badge on one line next to the clock; long labels ellipsis + tooltip */
.essay-status-badge {
  max-width: 42%;
}
</style>
